// src/components/ParticleNetwork.jsx — whisper-quiet particle network texture (landing background only)
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Tuning (background texture — visible but never competing with content):
// - DENSITY_PX2: ~1 particle per 21k px² of viewport
// - MAX_PARTICLES: hard cap for large / ultrawide screens
// - MIN_PARTICLES: floor so small phones still get a hint of texture
// - LINK_DIST: lines only under this short distance (px, CSS pixels)
// - SPEED: very slow drift (CSS px per frame @60fps ≈ 5–13 px/s)
// - DPR_CAP: keep GPU cost low on retina screens
// - Alphas: kept low on purpose — chunky shapes carry a lot of ink,
//   so 0.13–0.18 reads as texture, not foreground
const DENSITY_PX2 = 21000;
const MAX_PARTICLES = 110;
const MIN_PARTICLES = 30;
const LINK_DIST = 180;
const SPEED_MIN = 0.08;
const SPEED_MAX = 0.22;
const DOT_RADIUS_MIN = 5;
const DOT_RADIUS_MAX = 7;
const LINE_WIDTH = 4;
const DPR_CAP = 1.5;

const PALETTES = {
  light: { dot: '96, 165, 250', dotAlpha: 0.18, lineAlpha: 0.13 },
  dark: { dot: '186, 230, 253', dotAlpha: 0.18, lineAlpha: 0.13 }
};

function makeParticles(count, w, h) {
  const pts = [];
  for (let i = 0; i < count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN);
    pts.push({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      r: DOT_RADIUS_MIN + Math.random() * (DOT_RADIUS_MAX - DOT_RADIUS_MIN)
    });
  }
  return pts;
}

export default function ParticleNetwork({ theme = 'light', className = '' }) {
  const canvasRef = useRef(null);
  // Particle positions persist across theme toggles — generated once on
  // mount (+ on resize), never reseeded by a theme change.
  const particlesRef = useRef([]);
  // Live palette: updated every render, read by the draw loop, so a theme
  // toggle only recolors the existing field in place.
  const paletteRef = useRef(PALETTES.light);
  paletteRef.current = PALETTES[theme === 'dark' ? 'dark' : 'light'];
  // Repaint hook for the static (reduced-motion) fallback, which has no loop.
  const repaintRef = useRef(null);

  // Mount-only effect: canvas setup, field seeding, animation loop.
  // Theme is intentionally NOT a dependency — toggling it must not
  // regenerate positions or restart the loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let w = 0;
    let h = 0;
    let raf = 0;
    let running = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(
        MIN_PARTICLES,
        Math.min(MAX_PARTICLES, Math.floor((w * h) / DENSITY_PX2))
      );
      particlesRef.current = makeParticles(count, w, h);
    };

    const draw = () => {
      const palette = paletteRef.current;
      const particles = particlesRef.current;
      ctx.clearRect(0, 0, w, h);

      // Links first (under the dots), alpha fades with distance
      for (let i = 0; i < particles.length; i += 1) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j += 1) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > LINK_DIST) continue;
          const t = 1 - dist / LINK_DIST;
          ctx.strokeStyle = `rgba(${palette.dot}, ${(palette.lineAlpha * t).toFixed(3)})`;
          ctx.lineWidth = LINE_WIDTH;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Dots on top
      ctx.fillStyle = `rgba(${palette.dot}, ${palette.dotAlpha})`;
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = () => {
      const particles = particlesRef.current;
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        // Soft wrap at edges (margin exceeds max dot radius so chunks never pop)
        if (p.x < -8) p.x = w + 8;
        else if (p.x > w + 8) p.x = -8;
        if (p.y < -8) p.y = h + 8;
        else if (p.y > h + 8) p.y = -8;
      }
      draw();
    };

    const loop = () => {
      if (!running) return;
      if (!document.hidden) step();
      raf = requestAnimationFrame(loop);
    };

    resize();

    if (reduced) {
      // Static fallback: one still frame, no motion loop
      draw();
    } else {
      raf = requestAnimationFrame(loop);
    }

    const onVisibility = () => {
      // rAF already throttles in background tabs; the document.hidden
      // guard above skips work. Restart cleanly when returning.
      if (!reduced && !document.hidden && !raf) raf = requestAnimationFrame(loop);
    };
    const onResize = () => resize();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('resize', onResize);
    // Static repaint entry point (used on theme change when no loop runs).
    repaintRef.current = () => {
      if (w > 0 && h > 0) draw();
    };
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      raf = 0;
      repaintRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Theme change: repaint the EXISTING field with the new palette.
  // No reseeding — positions persist, dots stay where they are.
  // When the animation loop runs this is a harmless single extra frame;
  // under prefers-reduced-motion it is the repaint itself.
  useEffect(() => {
    if (repaintRef.current) repaintRef.current();
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none fixed inset-0 z-0 ${className}`}
    />
  );
}

ParticleNetwork.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string
};
